import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axiosClient from "../api/axiosClient";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [accessToken, setAccessToken] = useState(null);
    const [permissions, setPermissions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    const applyAuthResponse = useCallback((data) => {
        setAccessToken(data.accessToken);
        setUser(data.user);
        setUserData(data.user);
        setPermissions(data.permissions || []);
        window.__accessToken = data.accessToken;
    }, []);

    const signIn = async (email, password) => {
        const res = await axiosClient.post("/api/auth/login", { email, password });
        applyAuthResponse(res.data);
    };

    const signOut = useCallback(async () => {
        try {
            await axiosClient.post("/api/auth/logout", {}, { withCredentials: true });
        } catch (e) {
            console.warn("Logout request failed:", e);
        } finally {
            setAccessToken(null);
            setUser(null);
            setUserData(null);
            setPermissions([]);
            window.__accessToken = null;
        }
    }, []);

    const fetchUserData = async () => {
        setUserData(user);
    };

    useEffect(() => {
        const tryRefresh = async () => {
            try {
                const res = await axiosClient.__refreshSession();
                applyAuthResponse(res.data);
            } catch (failure) {
                if (failure.response?.status === 401) await signOut();
            } finally {
                setIsLoading(false);
            }
        };
        tryRefresh();
    }, [applyAuthResponse, signOut]);

    useEffect(() => {
        axiosClient.__setGlobalSignOut(signOut);
        axiosClient.__setAuthResponse(applyAuthResponse);
        return () => { axiosClient.__setGlobalSignOut(null); axiosClient.__setAuthResponse(null); };
    }, [signOut, applyAuthResponse]);

    return (
        <AuthContext.Provider
            value={{
                user,
                userData,
                setUserData,
                accessToken,
                permissions,
                signIn,
                signOut,
                fetchUserData,
                isLoading,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
